import { cn } from '@/lib/utils';
import { Loader2, Check } from 'lucide-react';
import { useFishingModalities } from '../hooks/useStoreMaterials';

interface ModalitySelectorProps {
    selected: number[];
    onChange: (modalities: number[]) => void;
    disabled?: boolean;
    compact?: boolean;
}

export function ModalitySelector({ selected, onChange, disabled, compact }: ModalitySelectorProps) {
    const { data: modalities, isLoading } = useFishingModalities();

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                <Loader2 size={12} className="animate-spin" />
                Cargando modalidades...
            </div>
        );
    }

    if (!modalities || modalities.length === 0) {
        return (
            <p className="text-xs text-muted-foreground/60 py-1">
                No hay modalidades disponibles
            </p>
        );
    }

    const allSelected = selected.length === modalities.length;

    const toggleModality = (id: number) => {
        if (disabled) return;
        if (selected.includes(id)) {
            onChange(selected.filter(m => m !== id));
        } else {
            onChange([...selected, id]);
        }
    };

    const toggleAll = () => {
        if (disabled) return;
        if (allSelected) {
            onChange([]);
        } else {
            onChange(modalities.map(m => m.id));
        }
    };

    return (
        <div className="space-y-2">
            {/* Select all button */}
            <button
                type="button"
                onClick={toggleAll}
                disabled={disabled}
                className={cn(
                    'text-[10px] font-medium transition-colors',
                    allSelected
                        ? 'text-primary hover:text-primary/80'
                        : 'text-muted-foreground hover:text-foreground',
                    disabled && 'opacity-50 cursor-not-allowed'
                )}
            >
                {allSelected ? '✓ Todas seleccionadas' : 'Seleccionar todas'}
            </button>

            {/* Modality chips */}
            <div className={cn('flex flex-wrap gap-1.5', compact && 'gap-1')}>
                {modalities.map(m => {
                    const isSelected = selected.includes(m.id);
                    return (
                        <button
                            key={m.id}
                            type="button"
                            onClick={() => toggleModality(m.id)}
                            disabled={disabled}
                            className={cn(
                                'inline-flex items-center gap-1 rounded-full border transition-all',
                                compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs',
                                'font-medium',
                                isSelected
                                    ? 'border-primary/50 bg-primary/10 text-primary'
                                    : 'border-border/50 bg-muted/20 text-muted-foreground hover:bg-muted/40 hover:text-foreground',
                                disabled && 'opacity-50 cursor-not-allowed'
                            )}
                        >
                            {isSelected && <Check size={compact ? 8 : 10} />}
                            {m.name}
                        </button>
                    );
                })}
            </div>

            {/* Counter */}
            {selected.length > 0 && (
                <p className="text-[10px] text-muted-foreground/60">
                    {selected.length} de {modalities.length} seleccionada{selected.length !== 1 ? 's' : ''}
                </p>
            )}
        </div>
    );
}
